import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { trackEvent } from 'src/lib/analytics';

import { hasAnyError, putInto, touchAllErrors } from './utils';

const FormContext = createContext(null);

export const FormProvider = ({ children, initValues, onSave, onValidate, debug }) => {
  const [{ loadingInitValues, initValuesLoadingError, initValuesValue }, setInitValuesResolution] = useState({
    loadingInitValues: typeof initValues === 'function',
    initValuesValue: typeof initValues === 'function' ? {} : initValues,
  });

  const [[values, touched, anyTouched], setState] = useState([!initValues || typeof initValues === 'function' ? {} : initValues, {}, false]);
  const [{ isSubmitting, submitError, submitResponse, isSubmitSuccessful }, setSubmittingStatus] = useState({});

  useEffect(() => {
    if (typeof initValues === 'function') {
      initValues()
        .then(initState => {
          setState(([values, touched, anyTouched]) => [initState, touched, false]);
          setInitValuesResolution({ initValuesValue: initState });
        })
        .catch(e => {
          setInitValuesResolution({ initValuesLoadingError: e });
        });
    }
  }, [initValues]);

  const onChange = useCallback(({ target: { name, value: newVal, checked, type } }) => {
    if (type === 'checkbox') newVal = checked;
    setState(([values, touched, anyTouched]) => [
      putInto(values, name, newVal),
      type === 'checkbox' ? putInto(touched, name, true) : touched,
      type === 'checkbox'
    ]);
  }, []);

  const onBlur = useCallback(({ target: { name, type } }) => {
    if (type === 'checkbox') return;
    setState(([values, touched, anyTouched]) => [values, putInto(touched, name, true), true]);
  }, []);

  const errors = onValidate ? onValidate(values) : {};

  const onSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    trackEvent('settings_manage_rule', { label: 'save' });
    if (hasAnyError(errors)) {
      setState(([values, touched, anyTouched]) => [values, touchAllErrors(errors), true]);
      return;
    }

    setSubmittingStatus({ isSubmitting: true, submitError: undefined, submitResponse: undefined, isSubmitSuccessful: undefined });
    onSave(values)
      .then(submitResponse => {
        setSubmittingStatus({ isSubmitting: false, submitResponse, isSubmitSuccessful: true });
        setInitValuesResolution({ initValuesValue: values });
        setState(([values, touched, anyTouched]) => [values, {}, false]);
      })
      .catch(submitError => setSubmittingStatus({ isSubmitting: false, submitError, isSubmitSuccessful: false }));
  };

  const resetToInitialState = useCallback(() => {
    setState([initValuesValue, {}, false]);
    setSubmittingStatus(status => ({ ...status, submitError: undefined, isSubmitSuccessful: undefined }));
  }, [initValuesValue]);

  if (debug) {
    console.log({ values, errors, touched });
  }

  return (
    <FormContext.Provider value={{ values,
      touched,
      errors,
      name: 'root',
      isSubmitting,
      isSubmitSuccessful,
      submitError,
      submitResponse,
      onChange,
      onBlur,
      onSubmit,
      anyTouched,
      resetToInitialState,
      initValues: initValuesValue,
      loadingInitValues,
      initValuesLoadingError }}>
      {children}
    </FormContext.Provider>
  );
};

export const useForm = () => useContext(FormContext);
